import type { PartDefinition, MaterialInfo } from '@rogue/rackbuilder'
import { AdjustOnlyVisual } from '../../constants'
import type { SquatStandOptions } from '../../RackTypes'
import { BarBuilder } from '../bar/barBuilder'
import { getRackModel } from '../../modelPaths'

export class SquatStandBuilder {
  protected config: SquatStandOptions
  protected line: SquatStandOptions['line']
  protected height: SquatStandOptions['height']
  protected color: SquatStandOptions['color']
  protected numbered: SquatStandOptions['numbered']
  protected hardware: SquatStandOptions['hardware']
  protected spotterArms: SquatStandOptions['spotterArms']
  protected pullupBar: SquatStandOptions['pullupBar']
  protected bar: SquatStandOptions['bar']
  protected bench: SquatStandOptions['bench']
  protected plates: SquatStandOptions['plates']
  protected rackLine: 'RM' | 'RML'
  
  protected squatStandLine: string
  protected material: MaterialInfo

  constructor(config: SquatStandOptions) {
    this.config = config
    this.line = config.line
    this.height = config.height
    this.color = config.color
    this.numbered = config.numbered
    this.hardware = config.hardware
    this.spotterArms = config.spotterArms
    this.pullupBar = config.pullupBar
    this.bar = config.bar
    this.bench = config.bench
    this.plates = config.plates
    
    this.squatStandLine = this.line === 'SML' ? 'SML' : 'SM'
    this.rackLine = this.line === 'SML' ? 'RML' : 'RM'
    
    // Create material after all properties are set
    this.material = this.createMaterial()
  }

  build(): PartDefinition {
    return this.assembleStand()
  }

  protected createMaterial(): MaterialInfo {
    if (this.color === 'satinClear') {
      return {
        main     : { material: 'satinClear' },
        hardware : { material: this.hardware },
      }
    }

    return {
      main     : { name: 'metal', material: 'mg', color: this.color },
      hardware : { material: this.hardware },
    }
  }

  protected shouldAddPullupBar(): boolean {
    return (this.height === '90' || this.height === '108') && !!this.pullupBar
  }

  protected getUprightMeshName(): string {
    let modePathName

    if (this.height === '90') {
      modePathName = 'SML_COMMON_PARTS'
    } else {
      modePathName = 'SML_UPRIGHT_VARIANTS'
    }

    if (this.numbered) {
      return getRackModel(modePathName, 'upright90 numbered')
    }

    return getRackModel(modePathName, `upright${this.height}`)
  }

  // Creates upright with all accessories
  protected createUpright(left: boolean): PartDefinition {
    const jcup = this.createJCup(left)

    const upright: PartDefinition = {
      mesh      : this.getUprightMeshName(),
      slots     : { 'jcup-high': jcup },
      anchor    : 'bottom',
      materials : this.material,
    }

    // Add spotter arms if configured
    if (this.spotterArms) {
      upright.slots.spotterarms = this.createSpotterArms(left)
    }

    // Add pullup bar (only to left side)
    if (left && this.shouldAddPullupBar()) {
      const pullupBar = this.createPullupBar()
      if (pullupBar) {
        upright.slots.top = pullupBar
      }
    }

    // Add sticker (only to left side for SML line)
    if (left && this.line === 'SML') {
      upright.slots.sticker_left = this.createUprightSticker()
    }

    return upright
  }

  protected createBase(left: boolean): PartDefinition {
    const base: PartDefinition = {
      mesh           : getRackModel('SML_COMMON_PARTS', 'base'),
      slots          : {},
      materials      : this.material,
      adjustments    : left ? [] : [{ type: 'flip', axis: 'Z' }],
      adjustmentType : left ? undefined : AdjustOnlyVisual,
    }

    // Add bench (only to left side)
    if (left && this.bench) {
      base.slots.bench = this.bench
    }

    // Add base sticker (only to left side)
    if (left) {
      base.slots['back-sticker'] = this.createBaseSticker()
    }

    return base
  }

  protected createBackStabilizer(): PartDefinition {
    return {
      mesh  : getRackModel(`${this.squatStandLine}_COMMON_PARTS`, 'back'),
      slots : {
        hardware: {
          mesh   : getRackModel(`${this.squatStandLine}_COMMON_PARTS`, 'stabilizer hardware'),
          slots  : {},
          anchor : 'back',
        },
      },
      materials: this.material,
    }
  }

  // MARK: Jcups
  protected createJCup(left: boolean): PartDefinition {
    const jcup: PartDefinition = {
      mesh        : getRackModel(`${this.rackLine}_JCUPS`, 'jcup'),
      slots       : {},
      adjustments : left ? [] : [
        { type: 'rotate', axis: 'Z', amount: 180 }, 
        { type: 'flip', axis: 'Y' },
      ],
    }

    // Add bar to left jcup only
    if (left && this.bar) {
      // Add plates to bar if both exist
      if (this.plates) {
        this.bar.plates = this.plates
      }
      jcup.slots.bar = new BarBuilder(this.bar).build()
    }

    return jcup
  }

  protected createSpotterArms(left: boolean): PartDefinition {
    return {
      mesh        : getRackModel(`${this.rackLine}_SAFETY_SPOTTER_ARMS`, 'safety spotter arms'),
      slots       : {},
      adjustments : left ? [] : [
        { axis: 'Z', type: 'rotate', amount: 180, pieces: ['clasp'] },
      ],
    }
  }

  // MARK: Pullup Bar
  protected createPullupBar(): PartDefinition | null {
    
    if (!this.pullupBar) return null
    
    const pullup = this.pullupBar === 'basic' ? 'skinny' : 'fat skinny'
    
    return {
      mesh      : getRackModel(`${this.rackLine}_PULLUP`, pullup),
      slots     : {},
      materials : this.material,
    }
  }

  protected createUprightSticker(): PartDefinition {
    return {
      mesh  : getRackModel('SML_COMMON_PARTS', 'upright sticker'),
      slots : {},
    }
  }

  protected createBaseSticker(): PartDefinition {
    return {
      mesh        : getRackModel('SML_COMMON_PARTS', 'base sticker'),
      slots       : {},
      adjustments : [
        { axis: 'X', type: 'rotate', amount: 180 },
        { axis: 'Z', type: 'rotate', amount: 90 },
        { axis: 'X', type: 'flip' },
      ],
      offset: { z: 1, x: -0.2 },
    }
  }

  // MARK: Build Aseembly
  protected assembleStand(): PartDefinition {
    const leftBase = this.createBase(true)
    const rightBase = this.createBase(false)
    const leftUpright = this.createUpright(true)
    const rightUpright = this.createUpright(false)

    // Assemble right side
    rightBase.slots.upright = rightUpright

    // Assemble left side
    leftBase.slots.upright = leftUpright
    leftBase.slots.otherside = rightBase
    leftBase.slots.backstabilizer = this.createBackStabilizer()

    return leftBase
  }
}